import React, { useState, useEffect } from 'react';
import {
  Card,
  Typography,
  Space,
  Button,
  Input,
  message,
  Spin,
  Progress,
  Empty,
  Tag,
} from 'antd';
import {
  SearchOutlined,
  AppstoreOutlined,
  BarsOutlined,
  ReloadOutlined,
  FolderOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { getAccHubs, getAccProjects } from '../../../services/api';
import { ProtectedRoute, PageGuard } from '../../../components/common/security/ProtectedRoute';

const { Title, Text } = Typography;

interface Project {
  id: string;
  name: string;
  hubId: string;
  hubName: string;
  type: string;
  status: string;
  createdAt?: string;
  updatedAt?: string;
}

const AccProjects: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [filteredProjects, setFilteredProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const navigate = useNavigate();

  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    filterProjects();
  }, [searchQuery, projects]);

  const loadProjects = async () => {
    try {
      setLoading(true);

      // Get hubs first
      const hubsResult = await getAccHubs();
      if (!hubsResult.success || !hubsResult.data?.data) {
        message.error('Hub\'lar yüklenemedi');
        return;
      }

      const hubsList = hubsResult.data.data;
      const allProjects: Project[] = [];

      // Get projects for each hub
      for (const hub of hubsList) {
        try {
          const projectsResult = await getAccProjects(hub.id);
          if (projectsResult.success && projectsResult.data?.data) {
            const hubProjects = projectsResult.data.data.map((project: any) => ({
              id: project.id,
              name: project.attributes?.name || 'İsimsiz Proje',
              hubId: hub.id,
              hubName: hub.attributes?.name || 'Bilinmeyen Hub',
              type: project.attributes?.extension?.type || 'ACC',
              status: 'active',
              createdAt: project.attributes?.extension?.data?.createdAt,
              updatedAt: project.attributes?.extension?.data?.lastModifiedTime,
            }));
            allProjects.push(...hubProjects);
          }
        } catch (err) {
          console.error(`Hub ${hub.id} projeleri yüklenemedi:`, err);
        }
      }

      // Filter out template/test/library projects
      const filtered = allProjects.filter((project) => {
        const nameLower = project.name.toLowerCase();
        const isTemplate = nameLower.includes('template');
        const isLibrary = nameLower.includes('library');
        const isSample = nameLower.includes('sample');
        const isTest = nameLower.startsWith('999-') || nameLower.startsWith('998-');
        const isComponent = nameLower.includes('component');
        return !isTemplate && !isLibrary && !isSample && !isTest && !isComponent;
      });

      // Sort alphabetically
      const sorted = filtered.sort((a, b) => 
        a.name.localeCompare(b.name, 'tr', { sensitivity: 'base' })
      );

      setProjects(sorted);
      setFilteredProjects(sorted);
    } catch (error: any) {
      message.error('Projeler yüklenirken hata: ' + (error.message || 'Bilinmeyen hata'));
    } finally {
      setLoading(false);
    }
  };

  const filterProjects = () => {
    if (!searchQuery.trim()) {
      setFilteredProjects(projects);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = projects.filter(
      (project) =>
        project.name.toLowerCase().includes(query) ||
        project.hubName.toLowerCase().includes(query)
    );
    setFilteredProjects(filtered);
  };

  const handleProjectClick = (project: Project) => {
    // Store project info in session storage
    sessionStorage.setItem(
      'selectedProject',
      JSON.stringify({
        id: project.id,
        name: project.name,
        hubId: project.hubId,
        hubName: project.hubName,
      })
    );
    navigate(`/design/acc/projects/${project.id}?hubId=${project.hubId}`);
  };

  const getInitial = (name: string) => {
    // Proje numarası varsa onu göster (örn: "005-TKF" -> "005")
    const match = name.match(/^(\d+)/);
    if (match) return match[1];
    return name.charAt(0).toUpperCase();
  };

  const getProjectColor = (name: string) => {
    const colors = [
      'bg-blue-100 text-blue-600',
      'bg-green-100 text-green-600',
      'bg-purple-100 text-purple-600',
      'bg-orange-100 text-orange-600',
      'bg-cyan-100 text-cyan-600',
      'bg-pink-100 text-pink-600',
    ];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  return (
    <ProtectedRoute>
      <PageGuard>
        <div style={{ padding: 24, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
          {/* Header */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: 24,
            }}
          >
            <div>
              <Title level={2} style={{ margin: 0 }}>
                ACC Projeleri
              </Title>
              <Text type="secondary">
                {filteredProjects.length} aktif proje
              </Text>
            </div>

            <Space>
              {/* View Mode Toggle */}
              <div
                style={{
                  display: 'flex',
                  backgroundColor: '#f0f0f0',
                  borderRadius: 8,
                  padding: 4,
                  border: '1px solid #e0e0e0',
                }}
              >
                <Button
                  type={viewMode === 'grid' ? 'primary' : 'text'}
                  icon={<AppstoreOutlined />}
                  onClick={() => setViewMode('grid')}
                  style={{ borderRadius: 6 }}
                />
                <Button
                  type={viewMode === 'list' ? 'primary' : 'text'}
                  icon={<BarsOutlined />}
                  onClick={() => setViewMode('list')}
                  style={{ borderRadius: 6 }}
                />
              </div>

              <Button icon={<ReloadOutlined />} onClick={loadProjects} loading={loading}>
                Yenile
              </Button>
            </Space>
          </div>

          {/* Search */}
          <div style={{ marginBottom: 24 }}>
            <Input
              placeholder="Proje ara..."
              prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ maxWidth: 400 }}
              size="large"
              allowClear
            />
          </div>

          {/* Content */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: 80 }}>
              <Spin size="large" />
              <div style={{ marginTop: 16 }}>
                <Text type="secondary">Projeler yükleniyor...</Text>
              </div>
            </div>
          ) : filteredProjects.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={searchQuery ? 'Arama sonucu bulunamadı' : 'Proje bulunamadı'}
            />
          ) : viewMode === 'grid' ? (
            /* Grid View */
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                gap: 16,
              }}
            >
              {filteredProjects.map((project) => (
                <Card
                  key={project.id}
                  hoverable
                  onClick={() => handleProjectClick(project)}
                  style={{ borderRadius: 12, overflow: 'hidden' }}
                  bodyStyle={{ padding: 20 }}
                >
                  {/* Header */}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: 16,
                    }}
                  >
                    <div
                      className={getProjectColor(project.name)}
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: 12,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 16,
                        fontWeight: 700,
                      }}
                    >
                      {getInitial(project.name)}
                    </div>
                    <Tag color="blue">{project.hubName}</Tag>
                  </div>

                  {/* Title */}
                  <Title level={5} style={{ marginBottom: 8, marginTop: 0 }}>
                    {project.name}
                  </Title>

                  {/* Meta Info */}
                  <Space direction="vertical" size={4} style={{ width: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <FolderOutlined style={{ color: '#8c8c8c', fontSize: 14 }} />
                      <Text type="secondary" style={{ fontSize: 13 }}>
                        {project.type}
                      </Text>
                    </div>
                    {project.updatedAt && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <CalendarOutlined style={{ color: '#8c8c8c', fontSize: 14 }} />
                        <Text type="secondary" style={{ fontSize: 13 }}>
                          {new Date(project.updatedAt).toLocaleDateString('tr-TR')}
                        </Text>
                      </div>
                    )}
                  </Space>

                  {/* Footer */}
                  <div
                    style={{
                      marginTop: 16,
                      paddingTop: 16,
                      borderTop: '1px solid #f0f0f0',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Tag color="green">Aktif</Tag>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            /* List View */
            <Card bodyStyle={{ padding: 0 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
                    <th
                      style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        fontWeight: 600,
                        fontSize: 13,
                        color: '#666',
                      }}
                    >
                      Proje Adı
                    </th>
                    <th
                      style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        fontWeight: 600,
                        fontSize: 13,
                        color: '#666',
                      }}
                    >
                      Hub
                    </th>
                    <th
                      style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        fontWeight: 600,
                        fontSize: 13,
                        color: '#666',
                      }}
                    >
                      Tür
                    </th>
                    <th
                      style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        fontWeight: 600,
                        fontSize: 13,
                        color: '#666',
                      }}
                    >
                      Son Güncelleme
                    </th>
                    <th
                      style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        fontWeight: 600,
                        fontSize: 13,
                        color: '#666',
                      }}
                    >
                      Durum
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProjects.map((project) => (
                    <tr
                      key={project.id}
                      onClick={() => handleProjectClick(project)}
                      style={{
                        cursor: 'pointer',
                        borderBottom: '1px solid #f0f0f0',
                        transition: 'background-color 0.2s',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#fafafa')}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'white')}
                    >
                      <td style={{ padding: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <div
                            className={getProjectColor(project.name)}
                            style={{
                              width: 36,
                              height: 36,
                              borderRadius: 8,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 14,
                              fontWeight: 600,
                            }}
                          >
                            {getInitial(project.name)}
                          </div>
                          <Text strong>{project.name}</Text>
                        </div>
                      </td>
                      <td style={{ padding: '16px' }}>
                        <Text type="secondary">{project.hubName}</Text>
                      </td>
                      <td style={{ padding: '16px' }}>
                        <Text type="secondary">{project.type}</Text>
                      </td>
                      <td style={{ padding: '16px' }}>
                        <Text type="secondary">
                          {project.updatedAt
                            ? new Date(project.updatedAt).toLocaleDateString('tr-TR')
                            : '-'}
                        </Text>
                      </td>
                      <td style={{ padding: '16px' }}>
                        <Tag color="green">Aktif</Tag>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </div>
      </PageGuard>
    </ProtectedRoute>
  );
};

export default AccProjects;
